import { useCallback, useRef, useMemo } from "react";
import type { TimelineProject, TimelineClip, TimelineTextOverlay, TimelineTransition, FilterState } from "@scroll/shared-types/timeline";
import { resolveOverlayAnchor } from "@scroll/shared-types/overlays";
import type { OverlayItem, MentionOverlayPayload, HashtagOverlayPayload } from "@scroll/shared-types/overlays";
import { getOverlayAnimationStyle } from "@scroll/shared-types/timeline-utils";
import { getVideoFilterStyle } from "../filters-drawer";

interface ActiveTransitionInfo {
  transition: TimelineTransition;
  progress: number;
  /** The clip becoming visible after the transition. Null for cuts and when
   * the transition fires past the last clip (shouldn't happen, defensive). */
  incomingClip: TimelineClip | null;
}

/**
 * Transition types where a real two-layer render meaningfully improves the
 * preview vs. the legacy single-layer-with-CSS-effects approach.
 *
 * Intentional split — this is NOT a half-finished port:
 *
 *   DUAL-LAYER (renders both outgoing + incoming clips):
 *     - crossfade       — outgoing fades, incoming reveals
 *     - slide-{l,r,u,d} — outgoing translates off, incoming sits below
 *     - wipe-{l,r}      — outgoing clipped progressively, incoming below
 *
 *   SINGLE-LAYER BY DESIGN (CSS effect on outgoing only — incoming layer
 *   would add no fidelity):
 *     - fade-to-black / fade-to-white — outgoing fades to color; the
 *       incoming clip only matters at progress=1 which is the cut boundary,
 *       so the existing CSS overlay matches the rendered output
 *     - zoom-in / zoom-out / spin     — transform-based effects on the
 *       outgoing layer; ffmpeg renders these by xfade without a meaningful
 *       second-layer concept until the boundary
 *     - blur / glitch                 — full-screen overlay effects
 *
 * If you add a new TimelineTransition.type, decide which bucket it falls
 * into and add it here OR to getTransitionOverlayStyle/getContentTransformStyle.
 */
const DUAL_LAYER_TRANSITIONS = new Set<TimelineTransition["type"]>([
  "crossfade",
  "slide-left",
  "slide-right",
  "slide-up",
  "slide-down",
  "wipe-left",
  "wipe-right",
]);

interface VideoPreviewPanelProps {
  project: TimelineProject;
  activeOverlays: TimelineTextOverlay[];
  activeFilter: FilterState | null;
  activeClip: TimelineClip | null;
  activeTransition: ActiveTransitionInfo | null;
  currentTime: number;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  linkOverlays?: OverlayItem[];
  onLinkOverlayMove?: (id: string, x: number, y: number) => void;
}

export default function VideoPreviewPanel({
  project,
  activeOverlays,
  activeFilter,
  activeClip,
  activeTransition,
  currentTime,
  videoRef,
  linkOverlays,
  onLinkOverlayMove,
}: VideoPreviewPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Compute preview dimensions based on aspect ratio
  const previewStyle = useMemo(() => {
    const { outputWidth, outputHeight } = project;
    const aspect = outputWidth / outputHeight;
    return { aspectRatio: `${aspect}` };
  }, [project]);

  const isImage = activeClip?.type === "image";
  const filterStyle = activeFilter ? { filter: getVideoFilterStyle(activeFilter) } : undefined;

  // Use a real dual-layer preview for transitions where a single CSS-effect
  // layer is a poor approximation (crossfade dims the outgoing instead of
  // revealing the incoming; slides/wipes hide the destination clip behind
  // a black void). Other transitions stay on the legacy CSS path because
  // they're effects that don't benefit from a second source.
  const dualLayerEnabled =
    !!activeTransition?.incomingClip &&
    DUAL_LAYER_TRANSITIONS.has(activeTransition.transition.type);
  const incomingClip = dualLayerEnabled ? activeTransition!.incomingClip : null;
  const outgoingLayerStyle = dualLayerEnabled
    ? getOutgoingLayerStyle(activeTransition!)
    : undefined;
  const incomingLayerStyle = dualLayerEnabled
    ? getIncomingLayerStyle(activeTransition!)
    : undefined;

  // Transition overlay style (for fade/blur/glitch effects — a div on top).
  // Suppressed when the dual-layer path is handling it.
  const overlayStyle =
    activeTransition && !dualLayerEnabled ? getTransitionOverlayStyle(activeTransition) : null;
  // Content transform style (for spatial transitions — applied to the content container).
  // Also suppressed for dual-layer types since each layer transforms itself.
  const contentTransform =
    activeTransition && !dualLayerEnabled ? getContentTransformStyle(activeTransition) : undefined;

  return (
    <div className="timeline-preview" ref={containerRef} data-testid="timeline-preview-canvas">
      <div
        className="relative h-full flex items-center justify-center"
        style={{ ...previewStyle, ...(contentTransform || {}) }}
      >
        {/* Incoming clip layer — rendered UNDERNEATH the outgoing video so
            slides/wipes reveal it as the outgoing layer moves away. */}
        {incomingClip && (
          <IncomingClipLayer
            clip={incomingClip}
            filterStyle={filterStyle}
            style={incomingLayerStyle}
          />
        )}
        {/* Outgoing video (always rendered so videoRef stays valid). When
            in a dual-layer transition window, this layer carries the
            transition transform/opacity to reveal the incoming layer. */}
        <video
          ref={videoRef}
          className="timeline-preview-video"
          style={isImage
            ? { ...filterStyle, ...outgoingLayerStyle, display: "none" }
            : { ...filterStyle, ...outgoingLayerStyle }}
          playsInline
          muted={false}
        />
        {isImage && activeClip && (
          <img
            src={activeClip.sourceUri}
            className="timeline-preview-video"
            style={{ ...filterStyle, ...outgoingLayerStyle }}
            alt=""
          />
        )}

        {/* Transition overlay (fade/blur/glitch). Suppressed on dual-layer path. */}
        {overlayStyle && (
          <div className="timeline-preview-transition" style={overlayStyle} />
        )}

        {/* Text overlay rendering */}
        <div className="timeline-preview-overlays" style={previewStyle}>
          {activeOverlays.map((overlay) => (
            <OverlayDisplay key={overlay.id} overlay={overlay} currentTime={currentTime} />
          ))}
          {linkOverlays?.map((item) => {
            if (item.type !== "mention" && item.type !== "hashtag") return null;
            return (
              <LinkOverlayDisplay
                key={item.id}
                item={item}
                containerRef={containerRef}
                onMove={onLinkOverlayMove}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LinkOverlayDisplay({
  item,
  containerRef,
  onMove,
}: {
  item: OverlayItem;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onMove?: (id: string, x: number, y: number) => void;
}) {
  // Hooks must run unconditionally — the type guard moves below the
  // useCallback so React always sees the same hook count, regardless of
  // whether the parent ends up rendering us for a non-link overlay.
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!onMove) return;
      const container = containerRef.current;
      if (!container) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = container.getBoundingClientRect();
      // Track offset between pointer and the overlay anchor so dragging starts
      // exactly where the chip was grabbed rather than snapping under the cursor.
      const pointerXPct = ((e.clientX - rect.left) / rect.width) * 100;
      const pointerYPct = ((e.clientY - rect.top) / rect.height) * 100;
      const offsetX = pointerXPct - item.x;
      const offsetY = pointerYPct - item.y;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);

      const handlePointerMove = (ev: PointerEvent) => {
        const r = container.getBoundingClientRect();
        const nx = ((ev.clientX - r.left) / r.width) * 100 - offsetX;
        const ny = ((ev.clientY - r.top) / r.height) * 100 - offsetY;
        const clampedX = Math.max(0, Math.min(100, nx));
        const clampedY = Math.max(0, Math.min(100, ny));
        onMove(item.id, clampedX, clampedY);
      };

      const handlePointerUp = (ev: PointerEvent) => {
        try { target.releasePointerCapture(ev.pointerId); } catch {}
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [containerRef, item.id, item.x, item.y, onMove],
  );

  if (item.type !== "mention" && item.type !== "hashtag") return null;
  const isMention = item.type === "mention";
  const label = isMention
    ? `@${(item.payload as MentionOverlayPayload).username}`
    : `#${(item.payload as HashtagOverlayPayload).name}`;
  const anchorTopLeft = resolveOverlayAnchor(item, "top-left") !== "center";
  const draggable = !!onMove;

  return (
    <div
      className={`timeline-preview-link-overlay${draggable ? " draggable" : ""}`}
      style={{
        left: `${item.x}%`,
        top: `${item.y}%`,
        transform: anchorTopLeft ? undefined : "translate(-50%, -50%)",
        opacity: item.opacity ?? 1,
      }}
      onPointerDown={handlePointerDown}
      role={draggable ? "button" : undefined}
      tabIndex={draggable ? 0 : undefined}
      aria-label={draggable ? `Drag ${label}` : undefined}
    >
      {label}
    </div>
  );
}

/**
 * Renders the incoming clip under the outgoing video layer during transitions
 * that benefit from a real two-layer preview. Pre-loads the video source so
 * the swap at the boundary is visual-only, no re-fetch. Muted to prevent
 * doubled audio with the live outgoing player.
 */
function IncomingClipLayer({
  clip,
  filterStyle,
  style,
}: {
  clip: TimelineClip;
  filterStyle?: React.CSSProperties;
  style?: React.CSSProperties;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Set src + seek to trimStart on mount/when clip changes so the first
  // frame visible at the start of the transition is the right one.
  // The preview is a low-fidelity reference; we don't try to keep this
  // layer in lockstep with the playhead — just landing on the correct
  // starting frame is enough to make crossfades/slides feel correct.
  const filterCombined = { ...(filterStyle || {}), ...(style || {}) };
  if (clip.type === "image") {
    return (
      <img
        src={clip.sourceUri}
        className="timeline-preview-video"
        style={filterCombined}
        alt=""
      />
    );
  }
  return (
    <video
      ref={videoRef}
      className="timeline-preview-video"
      style={filterCombined}
      src={clip.sourceUri}
      playsInline
      muted
      preload="auto"
      onLoadedMetadata={(e) => {
        const v = e.currentTarget;
        if (Number.isFinite(clip.trimStart) && clip.trimStart > 0) {
          try { v.currentTime = clip.trimStart; } catch {}
        }
      }}
    />
  );
}

/** Outgoing-layer transforms/opacity for dual-layer transitions. The incoming
 * layer sits beneath; whatever this layer hides/moves out of the way reveals
 * the incoming clip underneath. */
function getOutgoingLayerStyle(info: ActiveTransitionInfo): React.CSSProperties | undefined {
  const t = info.transition.type;
  const p = info.progress; // 0 → 1 over the transition window
  if (t === "crossfade") return { opacity: 1 - p };
  if (t === "slide-left") return { transform: `translateX(${-p * 100}%)` };
  if (t === "slide-right") return { transform: `translateX(${p * 100}%)` };
  if (t === "slide-up") return { transform: `translateY(${-p * 100}%)` };
  if (t === "slide-down") return { transform: `translateY(${p * 100}%)` };
  if (t === "wipe-left") return { clipPath: `inset(0 ${p * 100}% 0 0)` };
  if (t === "wipe-right") return { clipPath: `inset(0 0 0 ${p * 100}%)` };
  return undefined;
}

/** Incoming-layer styles for dual-layer transitions. For most transitions
 * the incoming layer just sits at full opacity/position — the outgoing layer
 * does all the work. Crossfade is the exception where incoming fades in. */
function getIncomingLayerStyle(info: ActiveTransitionInfo): React.CSSProperties {
  const t = info.transition.type;
  const p = info.progress;
  const base: React.CSSProperties = { position: "absolute", inset: 0 };
  if (t === "crossfade") return { ...base, opacity: p };
  // Slides and wipes: incoming is fully visible underneath; outgoing
  // transformation reveals it progressively.
  return base;
}

/** Convert 0→1 progress to a bounce intensity: ramps up in first half, back down in second */
function bounceIntensity(progress: number): number {
  return progress < 0.5 ? progress * 2 : (1 - progress) * 2;
}

/** Get CSS for a semi-transparent overlay div (fades, blur, glitch) */
function getTransitionOverlayStyle(
  info: ActiveTransitionInfo,
): React.CSSProperties | null {
  const { transition, progress } = info;
  const t = transition.type;
  const p = bounceIntensity(progress);

  // No "crossfade" branch here on purpose: crossfade is in
  // DUAL_LAYER_TRANSITIONS, so it always previews via the real two-layer
  // blend and never reaches this single-layer fallback. If you remove it
  // from that set, add a real handler here — a dark-dim approximation
  // would not match the rendered output.
  if (t === "fade-to-black") {
    return { backgroundColor: `rgba(0,0,0,${p})` };
  }
  if (t === "fade-to-white") {
    return { backgroundColor: `rgba(255,255,255,${p})` };
  }
  if (t === "blur") {
    return { backdropFilter: `blur(${p * 20}px)` };
  }
  if (t === "glitch") {
    return {
      backgroundColor: `rgba(255,0,100,${p * 0.3})`,
      mixBlendMode: "difference",
    };
  }

  return null;
}

/** Get CSS transform for spatial transitions (slide, zoom, spin, wipe) */
function getContentTransformStyle(
  info: ActiveTransitionInfo,
): React.CSSProperties | undefined {
  const { transition, progress } = info;
  const t = transition.type;

  const isOutgoing = progress < 0.5;
  const p = bounceIntensity(progress);

  if (t === "slide-left") {
    return { transform: `translateX(${isOutgoing ? -p * 100 : p * 100}%)`, overflow: "hidden" };
  }
  if (t === "slide-right") {
    return { transform: `translateX(${isOutgoing ? p * 100 : -p * 100}%)`, overflow: "hidden" };
  }
  if (t === "slide-up") {
    return { transform: `translateY(${isOutgoing ? -p * 100 : p * 100}%)`, overflow: "hidden" };
  }
  if (t === "slide-down") {
    return { transform: `translateY(${isOutgoing ? p * 100 : -p * 100}%)`, overflow: "hidden" };
  }
  if (t === "zoom-in") {
    const scale = 1 + p * 0.5;
    const opacity = 1 - p * 0.5;
    return { transform: `scale(${scale})`, opacity };
  }
  if (t === "zoom-out") {
    const scale = 1 - p * 0.4;
    const opacity = 1 - p * 0.5;
    return { transform: `scale(${scale})`, opacity };
  }
  if (t === "spin") {
    const angle = isOutgoing ? p * 180 : -p * 180;
    const scale = 1 - p * 0.3;
    return { transform: `rotate(${angle}deg) scale(${scale})` };
  }
  if (t === "wipe-left") {
    const clipPercent = isOutgoing ? 100 - p * 100 : p * 100;
    return { clipPath: `inset(0 ${100 - clipPercent}% 0 0)` };
  }
  if (t === "wipe-right") {
    const clipPercent = isOutgoing ? 100 - p * 100 : p * 100;
    return { clipPath: `inset(0 0 0 ${100 - clipPercent}%)` };
  }

  return undefined;
}

function OverlayDisplay({ overlay, currentTime }: { overlay: TimelineTextOverlay; currentTime: number }) {
  const anim = getOverlayAnimationStyle(overlay, currentTime);

  const centerShift = resolveOverlayAnchor(overlay, "center") === "center" ? "translate(-50%, -50%) " : "";
  const style: React.CSSProperties = {
    left: `${overlay.x}%`,
    top: `${overlay.y}%`,
    transform: `${centerShift}rotate(${overlay.rotation}deg) translateY(${anim.translateY}px) scale(${anim.scale})`,
    fontSize: `${overlay.fontSize}px`,
    fontFamily: `${overlay.fontFamily}, sans-serif`,
    color: overlay.color,
    backgroundColor: overlay.backgroundColor || "transparent",
    opacity: overlay.opacity * anim.opacity,
    fontWeight: overlay.bold ? "bold" : "normal",
    fontStyle: overlay.italic ? "italic" : "normal",
    textAlign: overlay.textAlign,
  };

  return (
    <div className="timeline-preview-overlay" style={style}>
      {overlay.text}
    </div>
  );
}
